import { AlertV2 } from 'components/atoms/AlertV2';
import Link from 'components/atoms/Link';
import layoutContext from 'components/organisms/Layout/layoutContext';
import useGetExpertLoggedInUserData from 'hooks/Authentication/useGetExpertLoggedInUserData';
import { useWorkspaces } from 'hooks/Workspace';
import useWorkspaceDetails from 'hooks/useWorkspaceDetails';
import { useNavigate } from 'react-router-dom';
import type { FC } from 'react';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useQueryClient } from 'react-query';
import useExpertPusher from 'hooks/Workspace/useExpertPusher';
import type { IAccountant } from 'types/accountant.types';
import { useTranslation } from 'react-i18next';
import { AccountCacheKeys, WorkspaceCacheKeys } from 'types/cacheKeys.types';
import { getStoredWorkspaceId } from 'utils/workspaceStorage';
import { EXPERT_ROUTES } from 'applications/expert/routing/expertRoutes';
import { getDefaultEmail } from 'utils/accountant';

type OwnerPayload =
  | string
  | {
      _id?: string;
      id?: string;
      accountantId?: string;
      email?: string;
      name?: string;
      first_name?: string;
      last_name?: string;
      firstName?: string;
      lastName?: string;
    };

type OwnershipTransferPayload = {
  workspace_id?: string;
  workspace_name?: string;
  previous_owner?: OwnerPayload;
  new_owner?: OwnerPayload;
};

type OwnershipTransferEvent = OwnershipTransferPayload & {
  data?: OwnershipTransferPayload;
};

type BannerState = {
  title: string;
  descriptionBeforeLink: string;
  descriptionAfterLink: string;
  /** Workspace (office) that was transferred – switch to it when user clicks "here" */
  workspaceId: string;
};

/** Only called when previous_owner is present (handler guards on it). */
const formatOwnerName = (owner: OwnerPayload): string => {
  if (typeof owner === 'string') return owner;

  const firstName = owner?.first_name || owner?.firstName || '';
  const lastName = owner?.last_name || owner?.lastName || '';
  const fullName = `${firstName} ${lastName}`.trim();

  return (
    fullName ||
    owner?.name ||
    owner?.email ||
    (owner?.accountantId ?? owner?._id ?? owner?.id)?.toString() ||
    ''
  );
};

const isCurrentOwner = (owner: OwnerPayload, accountant: IAccountant) => {
  const defaultEmail = getDefaultEmail(accountant.emails);
  if (typeof owner === 'string') {
    return owner.toLowerCase() === defaultEmail.toLowerCase();
  }

  const ownerEmail = owner.email?.toLowerCase();
  if (ownerEmail && ownerEmail === defaultEmail.toLowerCase()) return true;

  const ownerId = owner._id || owner.accountantId || owner.id;
  if (ownerId && ownerId === accountant._id) return true;

  return false;
};

const useOwnershipTransferBanner = () => {
  const channel = useExpertPusher();
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const { workspaces } = useWorkspaces();
  const { data: accountant } = useGetExpertLoggedInUserData();
  const [bannerState, setBannerState] = useState<BannerState | null>(null);

  const workspaceNameById = useMemo(() => {
    return new Map(
      workspaces.map((workspace) => [workspace._id, workspace.name]),
    );
  }, [workspaces]);

  useEffect(() => {
    if (!channel) return;

    const handler = (event: OwnershipTransferEvent) => {
      const payload = event?.data || event;
      if (
        !payload?.workspace_id ||
        !payload.new_owner ||
        !payload.previous_owner ||
        !accountant
      )
        return;

      if (!isCurrentOwner(payload.new_owner, accountant)) return;

      // Update cache so the new owner sees updated workspace and accountant data
      queryClient.invalidateQueries([WorkspaceCacheKeys.Workspaces]);
      queryClient.invalidateQueries([AccountCacheKeys.AccountantData]);

      const workspaceName =
        workspaceNameById.get(payload.workspace_id) ||
        payload.workspace_name ||
        '';
      if (!workspaceName) return;

      const previousOwnerName = formatOwnerName(payload.previous_owner);
      const title = t('workspace.ownership_transferred.title', {
        previousOwnerName,
        workspaceName,
      });
      const descriptionBeforeLink = t(
        'workspace.ownership_transferred.description_before_link',
      );
      const descriptionAfterLink = t(
        'workspace.ownership_transferred.description_after_link',
      );
      setBannerState({
        title,
        descriptionBeforeLink,
        descriptionAfterLink,
        workspaceId: payload.workspace_id,
      });
    };

    channel.bind('workspace.ownership.transferred', handler);

    return () => {
      channel.unbind('workspace.ownership.transferred', handler);
    };
  }, [accountant, queryClient, channel, workspaceNameById, t]);

  return {
    bannerState,
    dismiss: () => setBannerState(null),
  };
};

const OwnershipTransferBanner: FC = () => {
  const { bannerState, dismiss } = useOwnershipTransferBanner();
  const { t } = useTranslation();
  const layout = useContext(layoutContext);
  const { setWorkspaceId } = useWorkspaceDetails();
  const navigate = useNavigate();

  if (!bannerState) return null;

  const handleHereClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Update workspace in state/storage only (no navigation), then go to settings
    if (getStoredWorkspaceId() !== bannerState.workspaceId) {
      setWorkspaceId(bannerState.workspaceId);
    }
    navigate(EXPERT_ROUTES.SETTINGS_MEMBERS);
  };

  return (
    <div
      className="fixed z-50 right-4 top-4"
      style={{ left: layout.sidebarArea }}
    >
      <AlertV2
        type="warning"
        variant="filled"
        size="large-mid"
        title={bannerState.title}
        description={
          <>
            {bannerState.descriptionBeforeLink}
            <Link
              href={EXPERT_ROUTES.SETTINGS_MEMBERS}
              onClick={handleHereClick}
              className="border-b-2 border-dotted border-b-transparent hover:border-b-taxes-500 pb-1"
            >
              {t('workspace.ownership_transferred.link_label')}
            </Link>
            {bannerState.descriptionAfterLink}
          </>
        }
        dismissible
        onDismiss={dismiss}
      />
    </div>
  );
};

export default OwnershipTransferBanner;
